import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Inbox, RefreshCw } from 'lucide-react';
import { StatusChip, type StatusTone } from '../abf/components/StatusChip';
import { Header, Note, SmallButton, SummaryGrid } from '../abf/onboarding/review/contractReviewStyles';
import { INK } from '../abf/onboarding/review/reviewStyles';
import { GridTable, Panel, Shell, Table, Toolbar, money, when, words } from '../abf/board/boardShared';
import { loan360Path } from '../../utils/loanNavigation';
import { getPcBoard, pcErrorMessage } from './api';
import { PC_QUEUE_PATH, pcActionPath, pcHomePath } from './routes';
import type { PcBoard, PcBoardRow } from './types';

/**
 * The private credit board (PC workflow parity, slice 1): one row per facility with the recorded
 * commitment, funded and unfunded figures, the official position where a fund holds one, pricing,
 * maturity, the latest covenant tests, what waits and the one next action. The totals keep commitment,
 * funded principal, IBOR exposure and market value as different numbers.
 */

const Dense = styled(Table)`
  th, td { white-space: nowrap; }
  td.wrap { white-space: normal; min-width: 220px; }
  tr[data-critical='true'] td:first-child { box-shadow: inset 3px 0 #f87171; }
  tr[data-critical='true'] td.sticky { box-shadow: inset 3px 0 #f87171, 1px 0 0 ${INK.line}; }
  .muted { color: ${INK.faint}; }
  .sub { display: block; font-size: 11.5px; color: ${INK.muted}; margin-top: 2px; }
`;

const Three = styled.div`
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
  strong { display: block; margin-bottom: 6px; }
`;

const rate = (value: number | null | undefined) => value === null || value === undefined ? '—' : `${Number(value).toFixed(2)}%`;
const bps = (value: number | null | undefined) => value === null || value === undefined ? '—' : `${Number(value)} bps`;
const pct = (num: number | null | undefined, den: number | null | undefined) =>
  num === null || num === undefined || !den ? null : Math.round((Number(num) / Number(den)) * 100);

const covenantTone = (c: PcBoardRow['covenants']): StatusTone =>
  c.breached ? 'critical' : c.warning ? 'warning' : c.total === 0 || c.untested === c.total ? 'neutral' : 'done';
const covenantLabel = (c: PcBoardRow['covenants']) => {
  if (c.total === 0) return 'no covenants';
  const parts: string[] = [];
  if (c.breached) parts.push(`${c.breached} breached`);
  if (c.warning) parts.push(`${c.warning} warning`);
  if (c.passed) parts.push(`${c.passed} pass`);
  if (c.untested) parts.push(`${c.untested} untested`);
  return parts.join(' · ');
};
const statusTone = (row: PcBoardRow): StatusTone => row.nonAccrual ? 'critical' : row.status === 'ACTIVE' ? 'done' : 'neutral';
const facilityWords = (type: string | null) => !type ? '—' : ({
  FIRST_LIEN: 'First lien TL', SECOND_LIEN: 'Second lien', UNITRANCHE: 'Unitranche', REVOLVER: 'Revolver',
  DELAYED_DRAW: 'Delayed draw', MEZZANINE: 'Mezzanine',
} as Record<string, string>)[type] || words(type);

const PrivateCreditBoardPage: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const needsAttention = params.get('needsAttention') === 'true';
  const mine = params.get('mine') === 'true';
  const [board, setBoard] = useState<PcBoard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { setBoard(await getPcBoard(needsAttention, mine)); setError(null); }
    catch (failure) { setError(pcErrorMessage(failure, 'The board could not be loaded.')); }
    finally { setBusy(false); }
  }, [needsAttention, mine]);
  useEffect(() => { void load(); }, [load]);

  const setFilter = (key: string, on: boolean) => {
    const next = new URLSearchParams(params);
    if (on) next.set(key, 'true'); else next.delete(key);
    setParams(next, { replace: true });
  };

  const { live, defined } = useMemo(() => {
    const rows = board?.facilities ?? [];
    return { live: rows.filter(r => r.position || r.tickets.posted > 0), defined: rows.filter(r => !r.position && r.tickets.posted === 0) };
  }, [board]);

  if (!board) return <Shell><Note role={error ? 'alert' : 'status'}><p>{error || 'Loading the private credit board…'}</p></Note></Shell>;
  const t = board.totals;
  const sy = board.sinceYesterday;

  const row = (r: PcBoardRow) => {
    const utilisation = pct(r.fundedAmount, r.currentCommitment);
    return <tr key={r.loanId} data-critical={r.attention.critical > 0}>
      <td className="wrap sticky">
        <Link to={loan360Path('PRIVATE_CREDIT', r.loanId)}><strong>{r.borrowerName}</strong></Link>
        <span className="sub">{r.loanName || r.loanCode}{r.arrangementName ? ` · ${r.arrangementName}` : ''}{r.sponsorName ? ` · ${r.sponsorName}` : ''}</span>
      </td>
      <td>{facilityWords(r.facilityType)}<span className="sub">{r.portfolioCode || <span className="muted">no portfolio</span>}</span></td>
      <td><StatusChip tone={statusTone(r)} label={r.nonAccrual ? 'non-accrual' : words(r.status)} /></td>
      <td>{r.attention.open
        ? <Link to={`${PC_QUEUE_PATH}?loanId=${r.loanId}`}>{r.attention.open} open{r.attention.critical ? ` · ${r.attention.critical} critical` : ''}</Link>
        : <span className="muted">none</span>}</td>
      <td className="num">{money(r.currentCommitment, r.currency)}</td>
      <td className="num">{money(r.fundedAmount, r.currency)}{utilisation !== null && <span className="sub">{utilisation}% drawn</span>}</td>
      <td className="num">{money(r.unfundedAmount, r.currency)}{r.ddtlUndrawn ? <span className="sub">DDTL {money(r.ddtlUndrawn, r.currency)}{r.ddtlExpiry ? ` to ${r.ddtlExpiry}` : ''}</span> : null}</td>
      <td className="num">{r.position ? money(r.position.exposure, r.currency) : <span className="muted">no position</span>}{r.position && <span className="sub">as of {r.position.asOfDate}</span>}</td>
      <td className="num">{r.position ? money(r.position.marketValue, r.currency) : <span className="muted">—</span>}</td>
      <td>{rate(r.allInRate)}<span className="sub">{r.baseRate || '—'} + {bps(r.spreadBps)}{r.pendingPricingEffectiveDate ? ` · ${bps(r.pendingSpreadBps)} from ${r.pendingPricingEffectiveDate}` : ''}</span></td>
      <td>{r.maturityDate || <span className="muted">not recorded</span>}</td>
      <td><StatusChip tone={covenantTone(r.covenants)} label={covenantLabel(r.covenants)} />{r.covenants.lastTestPeriodEnd && <span className="sub">last test {r.covenants.lastTestPeriodEnd}</span>}</td>
      <td>{r.packet ? <>{words(r.packet.status)} v{r.packet.version}{r.packet.readiness != null && <span className="sub">{r.packet.readiness}% ready</span>}</> : <span className="muted">no packet</span>}</td>
      <td>{r.tickets.open ? `${r.tickets.open} open` : r.tickets.posted ? `${r.tickets.posted} posted` : <span className="muted">none</span>}</td>
      <td className="wrap">
        <SmallButton type="button" onClick={() => navigate(pcActionPath(r.loanId, r.nextAction.workspace, r.nextAction.query))}>{r.nextAction.title} →</SmallButton>
        <span className="sub">{r.nextAction.reason}</span>
      </td>
    </tr>;
  };

  return <Shell>
    <Header>
      {!embedded && <Link className="back" to={pcHomePath()}><ArrowLeft size={14} /> Private credit</Link>}
      <div className="heading">
        <div>
          <div className="eyebrow">Private credit · board</div>
          <h1>Every facility, what it holds and what waits</h1>
          <div className="meta"><span>Business date {board.businessDate}</span><span>{t.facilities} facilit{t.facilities === 1 ? 'y' : 'ies'}; {t.withPosition} held in a fund</span>{!embedded && <span>{t.basis}</span>}</div>
        </div>
        <div className="actions">
          <SmallButton type="button" onClick={() => navigate(PC_QUEUE_PATH)}><Inbox size={14} /> Work queue{t.attentionOpen ? ` (${t.attentionOpen})` : ''}</SmallButton>
          <SmallButton type="button" onClick={() => void load()} disabled={busy}><RefreshCw size={14} /> Refresh</SmallButton>
        </div>
      </div>
    </Header>

    <SummaryGrid aria-label="Board totals" style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' }}>
      <div><span>Commitment (recorded)</span><strong>{money(t.commitment)}</strong></div>
      <div><span>Funded (recorded)</span><strong>{money(t.funded)}</strong></div>
      <div><span>Unfunded (recorded)</span><strong>{money(t.unfunded)}</strong></div>
      <div><span>Official exposure</span><strong>{money(t.exposure)}</strong></div>
      <div><span>Market value</span><strong>{money(t.marketValue)}</strong></div>
      <div><span>Attention</span><strong style={{ color: t.attentionCritical ? '#f8a5a5' : undefined }}>{t.attentionOpen} open{t.attentionCritical ? ` · ${t.attentionCritical} critical` : ''}</strong></div>
    </SummaryGrid>

    <Toolbar>
      <label><input type="checkbox" checked={needsAttention} onChange={e => setFilter('needsAttention', e.target.checked)} /> Needs attention</label>
      <label><input type="checkbox" checked={mine} onChange={e => setFilter('mine', e.target.checked)} /> Mine</label>
      <span style={{ color: INK.faint, fontSize: 12 }}>{embedded ? t.basis : ''}</span>
    </Toolbar>

    {error && <Note role="alert">{error}</Note>}

    {/* F-PCP-011: fifteen no-wrap columns overflow the page; the ABF board's scroll container keeps the
        next action reachable and the facility column in view while the figures scroll. */}
    <GridTable><Dense>
      <thead><tr>
        <th className="sticky">Facility</th><th>Type · fund</th><th>Status</th><th>Attention</th>
        <th className="num">Commitment</th><th className="num">Funded</th><th className="num">Unfunded</th>
        <th className="num">Official exposure</th><th className="num">Market value</th>
        <th>All-in rate</th><th>Maturity</th><th>Covenants</th><th>IC packet</th><th>Tickets</th><th>Next action</th>
      </tr></thead>
      <tbody>
        {live.map(row)}
        {live.length === 0 && <tr><td colSpan={15} className="muted">No facility is held in a fund yet.</td></tr>}
      </tbody>
    </Dense></GridTable>

    {defined.length > 0 && <Panel>
      <h3 style={{ margin: '0 0 4px' }}>Defined, not yet held</h3>
      <p style={{ margin: '0 0 10px', color: INK.muted, fontSize: 13 }}>Reference data only: the facility and its terms exist, no acquisition has posted. They are not exposure.</p>
      <GridTable><Dense>
        <thead><tr><th>Facility</th><th>Type · fund</th><th>Commitment</th><th>Attention</th><th>Next step</th></tr></thead>
        <tbody>{defined.map(r => <tr key={r.loanId}>
          <td className="wrap"><Link to={loan360Path('PRIVATE_CREDIT', r.loanId)}>{r.borrowerName}</Link><span className="sub">{r.loanName || r.loanCode}</span></td>
          <td>{facilityWords(r.facilityType)}<span className="sub">{r.portfolioCode || <span className="muted">no portfolio</span>}</span></td>
          <td className="num">{money(r.currentCommitment, r.currency)}</td>
          <td>{r.attention.open ? <Link to={`${PC_QUEUE_PATH}?loanId=${r.loanId}`}>{r.attention.open} open</Link> : <span className="muted">none</span>}</td>
          <td className="wrap"><SmallButton type="button" onClick={() => navigate(pcActionPath(r.loanId, r.nextAction.workspace, r.nextAction.query))}>{r.nextAction.title} →</SmallButton><span className="sub">{r.nextAction.reason}</span></td>
        </tr>)}</tbody>
      </Dense></GridTable>
    </Panel>}

    <Panel>
      <h3 style={{ margin: '0 0 4px' }}>Since yesterday</h3>
      <p style={{ margin: '0 0 10px', color: INK.muted, fontSize: 13 }}>What the records say happened since {sy.since}: lifecycle events, tickets, IC decisions and documents read.</p>
      <Three>
        <div><strong>Lifecycle events</strong><Table><tbody>
          {sy.events.map(e => <tr key={e.id}><td><Link to={loan360Path('PRIVATE_CREDIT', e.loan_id)}>{e.borrower_name}</Link></td><td>{words(e.event_type)} {e.event_amount != null ? money(e.event_amount) : ''}</td><td className="muted">{e.event_date} · {words(e.status)} · {e.created_by || 'system'}</td></tr>)}
          {sy.events.length === 0 && <tr><td className="muted">No lifecycle event.</td></tr>}
        </tbody></Table></div>
        <div><strong>Tickets and decisions</strong><Table><tbody>
          {sy.trades.map(x => <tr key={`t${x.id}`}><td><Link to={loan360Path('PRIVATE_CREDIT', x.loan_id)}>{x.borrower_name}</Link></td><td>Ticket #{x.id} {words(x.transaction_type)} {money(x.notional_amount)}</td><td className="muted"><StatusChip tone={x.status === 'POSTED' ? 'done' : x.status === 'VOIDED' ? 'critical' : 'progress'} label={words(x.status)} /> {when(x.created_at)}</td></tr>)}
          {sy.decisions.map(d => <tr key={`d${d.id}`}><td><Link to={loan360Path('PRIVATE_CREDIT', d.loan_id, 'underwriting')}>{d.borrower_name}</Link></td><td>IC packet {words(d.event_type)}{d.to_status ? ` → ${words(d.to_status)}` : ''}</td><td className="muted">{d.actor || 'system'} · {when(d.created_at)}</td></tr>)}
          {sy.trades.length === 0 && sy.decisions.length === 0 && <tr><td className="muted">No ticket or decision.</td></tr>}
        </tbody></Table></div>
        <div><strong>Documents read</strong><Table><tbody>
          {sy.documents.map(d => <tr key={d.job_id}><td>{d.loan_id ? <Link to={loan360Path('PRIVATE_CREDIT', d.loan_id)}>{d.document_name}</Link> : d.document_name}</td><td>{words(d.document_type)}{d.template_version ? ` · template v${d.template_version}` : ''}</td><td className="muted"><StatusChip tone={d.booking_status === 'POSTED' ? 'done' : d.booking_status === 'BOOKING_FAILED' ? 'critical' : 'progress'} label={words(d.booking_status || d.status)} /> {when(d.completed_at)}</td></tr>)}
          {sy.documents.length === 0 && <tr><td className="muted">No document read.</td></tr>}
        </tbody></Table></div>
      </Three>
    </Panel>
  </Shell>;
};

export default PrivateCreditBoardPage;
